import React, { useCallback, useEffect, useState } from 'react';
import {
  Building2, CheckCircle2, AlertTriangle, Loader2, ExternalLink, ShieldCheck, Clock, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';
import { stripeErrorToast, isStripeErrorCode } from '../../utils/stripeErrors';

// Human-friendly "X minutes/hours/days ago" for the last successful Stripe sync
// timestamp, so the "we couldn't reach Stripe" banner gives the provider
// confidence in the cached status instead of feeling alarming.
function formatSyncedAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}

const STATUS_META = {
  not_connected: {
    variant: 'danger',
    badge: 'Not connected',
    icon: AlertTriangle,
    heading: 'Connect your bank account to receive payouts',
    body: (
      'Zuriya holds your earnings safely but we can\'t send them to your bank '
      + 'until you connect with Stripe. Takes about 2 minutes, Stripe handles all verification.'
    ),
    cta: 'Connect with Stripe',
  },
  reconnect_required: {
    variant: 'warn',
    badge: 'Reconnect needed',
    icon: AlertTriangle,
    heading: 'Your Stripe account needs reconnecting',
    body: (
      'We couldn\'t reach your Stripe account, it may have been closed or removed. '
      + 'Reconnect now to keep receiving payouts. Your earnings are safe in the meantime.'
    ),
    cta: 'Reconnect with Stripe',
  },
  action_required: {
    variant: 'danger',
    badge: 'Action required',
    icon: AlertTriangle,
    heading: 'Finish your Stripe onboarding',
    body: 'Stripe needs a few more details before they can pay you out. Pick up where you left off.',
    cta: 'Continue onboarding',
  },
  pending: {
    variant: 'warn',
    badge: 'Pending verification',
    icon: Clock,
    heading: "Stripe is verifying your details",
    body: 'This usually takes 1–2 working days. You can keep taking bookings in the meantime, your earnings are safe.',
    cta: null,
  },
  enabled: {
    variant: 'ok',
    badge: 'Enabled',
    icon: CheckCircle2,
    heading: 'Ready to receive payouts',
    body: 'Your Stripe account is fully verified. Payouts run every Monday at 04:00 UTC once your available balance reaches the threshold.',
    cta: null,
  },
};

const VARIANT_CLASSES = {
  danger: 'border-red-200 bg-red-50/60',
  warn: 'border-amber-200 bg-amber-50/60',
  ok: 'border-emerald-200 bg-emerald-50/60',
};

const BADGE_CLASSES = {
  danger: 'bg-red-100 text-red-700',
  warn: 'bg-amber-100 text-amber-700',
  ok: 'bg-emerald-100 text-emerald-700',
};

function friendlyRequirement(req) {
  const map = {
    'external_account': 'Add your bank account',
    'tos_acceptance.date': 'Accept Stripe\'s Terms of Service',
    'tos_acceptance.ip': 'Accept Stripe\'s Terms of Service',
    'individual.dob.day': 'Confirm your date of birth',
    'individual.dob.month': 'Confirm your date of birth',
    'individual.dob.year': 'Confirm your date of birth',
    'individual.first_name': 'Confirm your first name',
    'individual.last_name': 'Confirm your last name',
    'individual.address.line1': 'Confirm your address',
    'individual.address.city': 'Confirm your address',
    'individual.address.postal_code': 'Confirm your address',
    'business_profile.url': 'Add your business URL',
  };
  return map[req] || req.replaceAll('_', ' ');
}

export function StripeConnectCard({ onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async (refresh = false) => {
    try {
      const res = await api.get('/providers/me/stripe-connect/status', { params: refresh ? { refresh: true } : {} });
      setStatus(res.data);
      onStatusChange?.(res.data);
    } catch (err) {
      console.error('Stripe status fetch failed', err);
    } finally { setLoading(false); }
  }, [onStatusChange]);

  useEffect(() => {
    // First paint: cached read. Then a refresh in the background.
    (async () => {
      await load(false);
      await load(true);
    })();
  }, [load]);

  // If we came back from Stripe, auto-refresh and celebrate.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const onboarding = params.get('stripe_onboarding');
    if (onboarding === 'complete') {
      toast.success('Welcome back, checking your Stripe status...');
      load(true);
    } else if (onboarding === 'refresh') {
      toast.info('Link expired or cancelled, re-issuing a fresh one.');
    }
    if (onboarding) {
      // Clean the URL so a browser refresh doesn't re-trigger the toast.
      params.delete('stripe_onboarding');
      const newSearch = params.toString();
      const url = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, '', url);
    }
  }, [load]);

  const handleConnect = async () => {
    setStarting(true);
    try {
      const res = await api.post('/providers/me/stripe-connect/onboard');
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not generate onboarding link');
      }
    } catch (err) {
      toast.error(stripeErrorToast(err, 'Could not start Stripe onboarding'));
    } finally {
      setStarting(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const res = await api.post('/providers/me/stripe-connect/express-login');
      if (res.data?.url) window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      // `payout_account_issue` = backend already cleared the stale
      // stripe_account_id. Refresh so the CTA flips to "Reconnect with Stripe".
      if (isStripeErrorCode(err, 'payout_account_issue')) {
        toast.info(stripeErrorToast(err));
        load(true);
        return;
      }
      toast.error(stripeErrorToast(err, 'Could not open Stripe dashboard'));
    }
  };

  if (loading && !status) {
    return (
      <div className="card-surface p-6 flex items-center justify-center" data-testid="stripe-connect-card-loading">
        <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
      </div>
    );
  }

  const state = status?.status || 'not_connected';
  // When the backend has flagged a provider whose Stripe account is no longer
  // reachable (deleted / revoked / platform mismatch), override the meta to
  // the dedicated "reconnect needed" card so the CTA reads correctly.
  const isReconnect = status?.reconnect_required && state === 'not_connected';
  const effectiveKey = isReconnect ? 'reconnect_required' : state;
  const meta = STATUS_META[effectiveKey] || STATUS_META.not_connected;
  const Icon = meta.icon;
  const variantClass = VARIANT_CLASSES[meta.variant];
  const badgeClass = BADGE_CLASSES[meta.variant];
  const requirements = status?.requirements_due || [];

  return (
    <div
      className={`rounded-2xl border ${variantClass} p-5 sm:p-6`}
      data-testid="stripe-connect-card"
      data-status={effectiveKey}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-white border border-[#E6E4DD] flex items-center justify-center shrink-0">
          <Building2 className="h-5 w-5 text-[#1A1A1A]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Payout Method</h3>
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${badgeClass}`}
              data-testid="stripe-connect-status-badge"
            >
              <Icon className="h-3 w-3" />
              {meta.badge}
            </span>
          </div>
          <p className="text-sm font-medium text-[#1A1A1A]">{meta.heading}</p>
          <p className="text-sm text-stone-600 mt-1">{meta.body}</p>

          {state === 'action_required' && requirements.length > 0 && (
            <div className="mt-3 rounded-lg bg-white border border-red-200 p-3">
              <p className="text-xs font-medium text-red-700 uppercase tracking-wide mb-2">Outstanding</p>
              <ul className="space-y-1">
                {[...new Set(requirements.map(friendlyRequirement))].slice(0, 6).map((r) => (
                  <li key={r} className="text-xs text-stone-600 flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-400 mt-1.5 shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            {meta.cta && (
              <Button
                onClick={handleConnect}
                disabled={starting}
                className="bg-[#D4AF37] hover:bg-[#C49E2F] text-white gap-2"
                data-testid="stripe-connect-start-btn"
              >
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                {starting ? 'Opening Stripe...' : meta.cta}
              </Button>
            )}
            {state === 'enabled' && (
              <>
                <Button
                  variant="outline"
                  onClick={handleOpenDashboard}
                  className="border-[#E6E4DD] gap-2"
                  data-testid="stripe-dashboard-btn"
                >
                  <ExternalLink className="h-4 w-4" />Open Stripe dashboard
                </Button>
                <span className="inline-flex items-center gap-1 text-xs text-stone-400 self-center">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> KYC complete · GB · GBP
                </span>
              </>
            )}
            {state === 'pending' && (
              <Button
                variant="outline"
                onClick={() => load(true)}
                className="border-[#E6E4DD] gap-2"
                data-testid="stripe-refresh-btn"
              >
                <Loader2 className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Check status
              </Button>
            )}
          </div>

          {status?.error === 'stripe_api_unavailable' && (
            (() => {
              // Two distinct tones for the banner:
              //   - Underlying state still `enabled` → informational. The
              //     cached status is fresh and correct; the live re-check
              //     just didn't land. No action needed.
              //   - Anything else → genuine warning, because we can't
              //     reliably tell the provider what's going on.
              const ago = formatSyncedAgo(status?.synced_at);
              if (state === 'enabled') {
                return (
                  <p
                    className="text-xs text-stone-500 mt-3 flex items-center gap-1.5"
                    data-testid="stripe-live-check-failed-info"
                  >
                    <Info className="h-3 w-3" />
                    <span>
                      Showing your last verified status{ago ? ` from ${ago}` : ''}. We couldn't
                      reach Stripe for a live re-check, refreshing the page will try again.
                    </span>
                  </p>
                );
              }
              return (
                <p
                  className="text-xs text-amber-700 mt-3 flex items-center gap-1.5"
                  data-testid="stripe-live-check-failed-warn"
                >
                  <AlertTriangle className="h-3 w-3" />
                  <span>
                    Couldn't reach Stripe to verify live status
                    {ago ? `, last check was ${ago}` : ''}. Please retry in a moment.
                  </span>
                </p>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
}
